(() => {
  if (customElements.get('rdb-product-extras')) return;
  const transactions = new Map();
  const validId = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const properties = data => Object.fromEntries([...data].filter(([key,value]) => /^properties\[.*\]$/.test(key) && typeof value === 'string').map(([key,value]) => [key.slice(11,-1),value]));
  class RdbProductExtras extends HTMLElement {
    #controller;
    #busy = false;
    connectedCallback() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      this.addEventListener('click', this.#click, {signal:this.#controller.signal});
      this.closest('form')?.addEventListener('reset', () => queueMicrotask(() => {
        if (this.#busy) return;
        for (const button of this.querySelectorAll('[data-extra-id]')) button.setAttribute('aria-pressed','false');
        this.#refresh();
      }), {signal:this.#controller.signal});
      customElements.whenDefined('product-form-component').then(() => {
        if (!this.isConnected || this.#busy || !this.closest('product-form-component')?.accessoryAdapterReady) return;
        for (const button of this.querySelectorAll('[data-extra-id]')) button.disabled = false;
      });
      this.#refresh();
    }
    disconnectedCallback() { this.#controller?.abort(); }
    #click = event => {
      const button = event.target.closest('button');
      if (!button || button.disabled || this.#busy || !this.contains(button)) return;
      if (button.dataset.extraCategory) {
        for (const tab of this.querySelectorAll('[data-extra-category]')) tab.setAttribute('aria-pressed',String(tab===button));
        for (const panel of this.querySelectorAll('[data-extra-panel]')) panel.hidden = panel.dataset.extraPanel !== button.dataset.extraCategory;
      }
      if (button.dataset.extraId && !button.disabled) button.setAttribute('aria-pressed',String(button.getAttribute('aria-pressed')!=='true'));
      if (button.dataset.extraRemove) {
        const card = [...this.querySelectorAll('[data-extra-id]')].find(el=>el.dataset.extraId===button.dataset.extraRemove);
        card?.setAttribute('aria-pressed','false');
        this.querySelector('[data-extra-category][aria-pressed="true"]')?.focus();
      }
      this.#refresh();
    };
    get selection() { return [...this.querySelectorAll('[data-extra-id][aria-pressed="true"]:enabled')]; }
    #refresh() {
      const selected=this.selection;
      for (const button of this.querySelectorAll('[data-extra-id]')) {
        button.querySelector('[data-extra-action]').textContent=button.getAttribute('aria-pressed')==='true'?this.dataset.addedLabel:this.dataset.addLabel;
      }
      const list=this.querySelector('[data-extra-selected]');list.replaceChildren();
      for(const item of selected){
        const button=document.createElement('button');button.type='button';button.dataset.extraRemove=item.dataset.extraId;
        button.setAttribute('aria-label',this.dataset.removeLabel.replace('__PRODUCT__',item.dataset.extraTitle));
        const text=document.createElement('span');text.textContent=item.dataset.extraTitle;button.append(text);
        button.insertAdjacentHTML('beforeend','<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg>');
        list.append(button);
      }
      this.querySelector('[data-extra-selection]').hidden=!selected.length;
      const sum=selected.reduce((n,b)=>n+Number(b.dataset.extraPrice),0);
      const total=this.querySelector('[data-extra-total]');
      total.dataset.rdbPrice=String(sum);
      if(window.rdbCurrencyPrices)window.rdbCurrencyPrices.refresh();
      else total.querySelector('[data-rdb-price-amount]').textContent=new Intl.NumberFormat(this.dataset.locale,{minimumFractionDigits:2,maximumFractionDigits:2}).format(sum/100);
    }
    static buildItems(formData, extras, greeting, quantityDefault=1, group=crypto.randomUUID()) {
      const id=Number(formData.get('id'));const quantity=Number(formData.get('quantity')||quantityDefault);
      if(!validId(id)||!Number.isInteger(quantity)||quantity<1)throw Error('Invalid main product');
      const paid=greeting?.querySelector('[data-card-design]:checked');
      let items;
      if(paid && paid.dataset.cardDesign!=='message'){
        if(paid.disabled||paid.dataset.cardAvailable!=='true'||!validId(paid.dataset.cardVariantId))throw Error('Unavailable greeting card');
        const Card=customElements.get('rdb-greeting-card-options');
        if(!Card?.buildItems)throw Error('Card adapter unavailable');
        items=Card.buildItems(formData,{variantId:paid.dataset.cardVariantId,message:greeting.querySelector('[data-card-message]')?.value||''},group,greeting.dataset.cardProperty,greeting.dataset.messageProperty,quantityDefault);
      }else{
        const parent={id,quantity,properties:properties(formData)};
        const plan=formData.get('selling_plan');if(plan){if(!validId(plan))throw Error('Invalid selling plan');parent.selling_plan=Number(plan);}
        items=[parent];
      }
      items[0].properties._rdb_extras_group=group;
      items[0].properties._rdb_extras_role='parent';
      for(const item of items.slice(1)){item.properties._rdb_extras_group=group;item.properties._rdb_extras_role='card';}
      const seen=new Set(items.map(item=>item.id));
      for(const extra of extras){
        if(!validId(extra.id)||seen.has(Number(extra.id)))throw Error('Invalid accessory');
        seen.add(Number(extra.id));
        items.push({id:Number(extra.id),quantity:1,parent_id:id,properties:{_rdb_extras_group:group,_rdb_extras_role:'accessory'}});
      }
      return items;
    }
    static inspectItems(actual,expected){
      if(!Array.isArray(actual))return 'unknown';
      const group=expected[0].properties._rdb_extras_group;
      const found=actual.filter(item=>item.properties?._rdb_extras_group===group);
      if(!found.length)return 'absent';
      if(found.length!==expected.length)return 'partial';
      const parent=found.find(item=>item.properties?._rdb_extras_role==='parent');
      if(!parent?.key)return 'partial';
      for(const wanted of expected){
        const got=found.find(item=>Number(item.variant_id||item.id)===wanted.id&&item.properties?._rdb_extras_role===wanted.properties._rdb_extras_role);
        if(!got||Number(got.quantity)!==wanted.quantity)return 'partial';
        const clean=x=>Object.entries(x||{}).filter(([,v])=>v!==''&&v!=null);
        if(clean(got.properties).length!==clean(wanted.properties).length||clean(wanted.properties).some(([k,v])=>String(got.properties[k])!==String(v)))return 'partial';
        if(wanted.parent_id&&got.parent_relationship?.parent_key!==parent.key)return 'partial';
        if(String(got.selling_plan_allocation?.selling_plan?.id||'')!==String(wanted.selling_plan||''))return 'partial';
      }
      return 'complete';
    }
    submitCartRequest({formData,url,sectionIds,quantityDefault,greetingCards}){
      const key=`${location.pathname}:${this.closest('form')?.id}:${this.dataset.productId}`;
      const previous=transactions.get(key);
      if(previous?.pending)return previous.pending;
      if(!previous&&!this.selection.length)return null;
      const selectedExtras=this.selection.map(el=>({id:el.dataset.extraId}));
      const controls=[...this.querySelectorAll('[data-extra-id],[data-extra-remove],[data-extra-category]')].map(button=>({button,disabled:button.disabled}));
      this.#busy=true;this.setAttribute('aria-busy','true');
      for(const {button} of controls)button.disabled=true;
      const error=(review,original)=>{
        this.querySelector('[data-extra-review]').hidden=!review;
        const message=review?this.dataset.reviewError:original?.description||this.dataset.error;
        return {status:422,message,description:message,rdbAddedItemCount:0};
      };
      const sections=[...new Set(sectionIds)].slice(0,5);
      const read=async()=>{const r=await fetch(this.dataset.cartUrl+'.js',{headers:{Accept:'application/json'},cache:'no-store',signal:AbortSignal.timeout(15000)});if(!r.ok)throw Error('Cart read failed');return r.json();};
      const success=async(transaction,response)=>{
        let rendered=response?.sections;
        if(!rendered&&sections.length){try{const u=new URL(location.href);u.searchParams.set('sections',sections.join(','));const r=await fetch(u.pathname+u.search,{signal:AbortSignal.timeout(15000)});if(r.ok)rendered=await r.json();}catch{}}
        transactions.delete(key);this.querySelector('[data-extra-review]').hidden=true;
        return {items:response?.items||[],sections:rendered,rdbAddedItemCount:transaction.items.reduce((n,x)=>n+x.quantity,0)};
      };
      const transaction=previous||{items:null,pending:null};
      transactions.set(key,transaction);
      const run=async()=>{
        if(previous){try{const cart=await read();const state=RdbProductExtras.inspectItems(cart.items,previous.items);if(state==='complete')return success(previous,cart);if(state!=='absent')return error(true);transactions.delete(key);return error(false);}catch{return error(true);}}
        try{transaction.items=RdbProductExtras.buildItems(formData,selectedExtras,greetingCards,quantityDefault);}catch{transactions.delete(key);return error(false);}
        let response;
        try{
          const result=await fetch(url,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:transaction.items,sections,sections_url:location.pathname+location.search}),signal:AbortSignal.timeout(20000)});
          response=await result.json();
          if(result.ok&&!response.status&&RdbProductExtras.inspectItems(response.items,transaction.items)==='complete')return success(transaction,response);
        }catch{}
        try{const cart=await read();const state=RdbProductExtras.inspectItems(cart.items,transaction.items);if(state==='complete')return success(transaction,response);if(state==='absent'){transactions.delete(key);return error(false,response);}return error(true);}catch{return error(true);}
      };
      transaction.pending=Promise.resolve().then(run).finally(()=>{
        transaction.pending=null;this.#busy=false;this.removeAttribute('aria-busy');
        for(const {button,disabled} of controls)button.disabled=disabled;
      });
      return transaction.pending;
    }
  }
  customElements.define('rdb-product-extras',RdbProductExtras);
})();
